import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Check, CheckCircle2, Download, Loader2, SprayCan, Upload, X, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { adminAPI, apiError } from '@/lib/adminApi';
import { readFileAsBase64, saveBlob } from '@/lib/files';

const FILTERS = [
  { key: 'pending', label: 'Waiting for review' },
  { key: 'approved', label: 'On the list' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

/**
 * The NAFDAC master list: upload NAFDAC's data (matched by NAFDAC number;
 * NAFDAC's details win), browse the list, and review products distributors
 * added with numbers the list doesn't have yet. Each product's spray
 * details (from its label) are what farmers' spray plans are built from.
 */
export default function AdminMasterListPage() {
  const [status, setStatus] = useState('pending');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [data, setData] = useState({ items: [], total: 0, counts: {}, has_more: false });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(timer);
  }, [q]);

  const load = useCallback(async (offset = 0) => {
    setLoading(true);
    try {
      const res = await adminAPI.masterList({ status, q: query, limit: 50, offset });
      setData((prev) => (offset ? { ...res.data, items: [...prev.items, ...res.data.items] } : res.data));
    } finally {
      setLoading(false);
    }
  }, [status, query]);

  useEffect(() => { void load(0).catch(() => {}); }, [load]);

  return (
    <div className="p-4 space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-bold">NAFDAC master list</h1>
        <p className="text-sm text-gray-600">
          Distributors tick products from this list. Products they add with a NAFDAC number
          that isn&apos;t on it wait here for review.
        </p>
      </div>

      <UploadPanel onDone={() => load(0)} />

      <div className="rounded-2xl border bg-white p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {FILTERS.map((f) => (
            <button key={f.key} type="button" onClick={() => setStatus(f.key)}
                    className={`rounded-full px-3 py-1.5 text-sm font-medium border ${
                      status === f.key ? 'bg-farm-primary text-white border-farm-primary' : 'bg-white text-gray-700'}`}
                    data-testid={`master-filter-${f.key}`}>
              {f.label}{f.key !== 'all' ? ` (${data.counts?.[f.key] ?? 0})` : ''}
            </button>
          ))}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, NAFDAC number, ingredient, distributor"
                 className="h-9 max-w-sm ml-auto" data-testid="master-admin-search" />
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-gray-500 border-b">
              <th className="py-2 pr-2">Product</th>
              <th className="py-2 pr-2">NAFDAC number</th>
              <th className="py-2 pr-2">Expires</th>
              <th className="py-2 pr-2">Details</th>
              <th className="py-2 pr-2">Spray plan</th>
              <th className="py-2 pr-2 text-right">Stocked by</th>
              <th className="py-2" />
            </tr>
          </thead>
          <tbody>
            {data.items.map((item) => (
              <Row key={item.id} item={item} onChanged={() => load(0)} />
            ))}
          </tbody>
        </table>
        {!loading && data.items.length === 0 && <p className="text-center text-gray-600 py-6">Nothing here.</p>}
        {loading && <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin" /></div>}
        {data.has_more && !loading && (
          <Button variant="outline" className="w-full mt-3" onClick={() => load(data.items.length)}>Show more</Button>
        )}
      </div>
    </div>
  );
}

function Row({ item, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [editSpray, setEditSpray] = useState(false);
  const review = async (action) => {
    let note = null;
    if (action === 'reject') {
      note = window.prompt('Why is it rejected? The distributor will see this.', '');
      if (note === null) return;
    }
    setBusy(true);
    setError('');
    try {
      await adminAPI.masterReview(item.id, action, note);
      onChanged();
    } catch (err) {
      setError(apiError(err, 'Could not save the review.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <tr className="border-b align-top" data-testid={`master-row-${item.nafdac_number}`}>
      <td className="py-2 pr-2">
        <p className="font-medium">{item.name}</p>
        <p className="text-xs text-gray-500">{item.category}</p>
        {item.submitted_by_name && <p className="text-xs text-gray-500">Added by {item.submitted_by_name}</p>}
        {item.review_note && <p className="text-xs text-red-700">Note: {item.review_note}</p>}
      </td>
      <td className="py-2 pr-2 font-mono">{item.nafdac_number}</td>
      <td className={`py-2 pr-2 ${item.expired ? 'text-red-700 font-semibold' : ''}`}>
        {item.nafdac_expiry || '—'}{item.expired ? ' (expired)' : ''}
      </td>
      <td className="py-2 pr-2 text-xs text-gray-600">
        {[item.manufacturer_name, item.active_ingredients, item.net_contents].filter(Boolean).join(' · ') || '—'}
      </td>
      <td className="py-2 pr-2">
        <button type="button" onClick={() => setEditSpray(true)}
                className={`text-xs font-semibold underline ${item.has_spray_plan ? 'text-green-700' : 'text-gray-500'}`}
                data-testid={`master-spray-${item.nafdac_number}`}>
          {item.has_spray_plan
            ? `Every ${item.spray.interval_days} days, ${item.spray.max_applications}×`
            : 'Add spray details'}
        </button>
        {editSpray && (
          <SprayDialog item={item} onClose={() => setEditSpray(false)}
                       onSaved={() => { setEditSpray(false); onChanged(); }} />
        )}
      </td>
      <td className="py-2 pr-2 text-right">{item.stocked_by}</td>
      <td className="py-2 text-right whitespace-nowrap">
        {item.master_status === 'pending' ? (
          <>
            <Button size="sm" className="bg-farm-primary mr-1" disabled={busy} onClick={() => review('approve')}
                    data-testid={`master-approve-${item.nafdac_number}`}>
              <Check className="w-4 h-4 mr-1" /> Approve
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => review('reject')}
                    data-testid={`master-reject-${item.nafdac_number}`}>
              <X className="w-4 h-4 mr-1" /> Reject
            </Button>
          </>
        ) : (
          <span className={`text-xs font-semibold ${item.master_status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>
            {item.master_status === 'approved' ? 'On the list' : 'Rejected'}
          </span>
        )}
        {error && <p className="text-xs text-red-700">{error}</p>}
      </td>
    </tr>
  );
}

const SPRAY_FIELDS = [
  { key: 'interval_days', label: 'Spray every (days)', type: 'number', placeholder: 'e.g. 14' },
  { key: 'max_applications', label: 'Most sprays per season', type: 'number', placeholder: 'e.g. 3' },
  { key: 'dose_per_hectare', label: 'Dose per hectare', placeholder: 'e.g. 1 L in 200 L of water' },
  { key: 'dose_per_knapsack', label: 'Dose per 15 L knapsack', placeholder: 'e.g. 50 ml' },
  { key: 'pre_harvest_days', label: 'Days before harvest (pre-harvest interval)', type: 'number', placeholder: 'e.g. 21' },
  { key: 'reentry_hours', label: 'Keep out of the field (hours)', type: 'number', placeholder: 'e.g. 24' },
  { key: 'crops', label: 'Crops on the label', placeholder: 'e.g. maize, rice' },
  { key: 'notes', label: 'Other label notes' },
];

/** The product's spray details from its label. Empty everything to clear them. */
function SprayDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState(() => Object.fromEntries(
    SPRAY_FIELDS.map((f) => [f.key, item.spray?.[f.key] ?? ''])));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, String(v).trim() || null]));
      await adminAPI.masterSpray(item.id, body);
      onSaved();
    } catch (err) {
      setError(apiError(err, 'Could not save the spray details.'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="master-spray-dialog">
        <DialogTitle className="flex items-center gap-2"><SprayCan className="w-5 h-5" /> Spray details: {item.name}</DialogTitle>
        <p className="text-sm text-gray-600">
          From the product label. Farmers&apos; spray plans need the interval, the number of sprays and a dose;
          without them the farmer sees &quot;Spray schedule is not available for this product&quot;.
        </p>
        <div className="space-y-3">
          {SPRAY_FIELDS.map((f) => (
            <label key={f.key} className="block text-sm">
              <span className="font-medium">{f.label}</span>
              <Input type={f.type || 'text'} min={f.type ? 0 : undefined} value={form[f.key]}
                     placeholder={f.placeholder} className="mt-1"
                     onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                     data-testid={`master-spray-${f.key}`} />
            </label>
          ))}
        </div>
        {error && <p className="text-sm text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-farm-primary" disabled={busy} onClick={save} data-testid="master-spray-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UploadPanel({ onDone }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const run = async (commit, picked = file) => {
    setBusy(true);
    setError('');
    try {
      const res = await adminAPI.masterUpload(picked.name, await readFileAsBase64(picked), commit);
      setReport(res.data);
      if (commit) onDone();
    } catch (err) {
      setReport(null);
      setError(apiError(err, 'Could not read the file.'));
    } finally {
      setBusy(false);
    }
  };
  const choose = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    setFile(picked);
    run(false, picked);
  };
  const template = async () => {
    const res = await adminAPI.masterTemplate();
    saveBlob(res.data, 'African-Farm-NAFDAC-Master-List.xlsx');
  };
  const s = report?.summary;
  const problems = report?.rows.filter((r) => !r.ok) || [];

  return (
    <div className="rounded-2xl border bg-white p-4">
      <p className="font-semibold">Upload NAFDAC data</p>
      <p className="text-sm text-gray-600 mb-3">
        One row per NAFDAC number (.xlsx or .csv). Numbers already on the list are updated with NAFDAC&apos;s
        details; a distributor&apos;s product waiting for review with the same number is approved.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={template} data-testid="master-template"><Download className="w-4 h-4 mr-1" /> Template</Button>
        <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={choose} data-testid="master-file" />
        <Button className="bg-farm-primary" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="master-choose">
          {busy && !report ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1" /> Choose file</>}
        </Button>
      </div>
      {error && <p className="text-sm text-red-700 mt-2">{error}</p>}
      {report && (
        <div className="mt-3 text-sm" data-testid="master-upload-report">
          <p className="font-medium flex items-center gap-1">
            {report.committed ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : null}
            {report.committed
              ? `Done: ${s.added} added, ${s.updated} updated, ${s.errors} rows skipped.`
              : `${file?.name}: ${s.total} rows, ${s.ok} ready, ${s.errors} with problems.`}
          </p>
          {problems.length > 0 && (
            <ul className="mt-2 max-h-48 overflow-y-auto space-y-1">
              {problems.map((r) => (
                <li key={r.row} className="flex gap-1 text-red-700">
                  <XCircle className="w-4 h-4 shrink-0" /> Row {r.row}{r.nafdac_number ? ` (${r.nafdac_number})` : ''}: {r.errors.join('; ')}
                </li>
              ))}
            </ul>
          )}
          {!report.committed && s.ok > 0 && (
            <Button className="mt-3 bg-farm-primary" disabled={busy} onClick={() => run(true)} data-testid="master-commit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Apply ${s.ok} rows`}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
